interface DragResizeOptions {
    dragHandle?: string;
    minWidth?: number;
    minHeight?: number;
    onDragStart?: () => void;
    onDragEnd?: () => void;
    onResizeStart?: () => void;
    onResizeEnd?: () => void;
}

class DraggableResizable {
    private container: HTMLElement;
    private isDragging = false;
    private isResizing = false;
    private dragStartX = 0;
    private dragStartY = 0;
    private dragStartLeft = 0;
    private dragStartTop = 0;
    private resizeStartX = 0;
    private resizeStartY = 0;
    private resizeStartWidth = 0;
    private resizeStartHeight = 0;
    private options: DragResizeOptions;
    private dragMouseMoveHandler: ((e: MouseEvent) => void) | null = null;
    private dragMouseUpHandler: (() => void) | null = null;
    private resizeMouseMoveHandler: ((e: MouseEvent) => void) | null = null;
    private resizeMouseUpHandler: (() => void) | null = null;

    constructor(containerSelector: string, options: DragResizeOptions = {}) {
        this.container = document.querySelector(containerSelector) as HTMLElement;
        if (!this.container) return;

        this.options = {
            minWidth: 400,
            minHeight: 300,
            ...options
        };

        this.initialize();
    }

    private initialize(): void {
        // Make container position absolute
        this.container.style.position = 'absolute';
        // Ensure box-sizing is consistent so width/height set via JS match getBoundingClientRect
        this.container.style.boxSizing = 'border-box';
        // Remove any CSS max constraints that would prevent resize from taking effect
        this.container.style.maxWidth = 'none';
        this.container.style.maxHeight = 'none';
        // Set initial size to the supplied min dimensions
        this.container.style.width = `${this.options.minWidth}px`;
        this.container.style.height = `${this.options.minHeight}px`;

        // Setup drag handle
        const dragHandle = this.options.dragHandle 
            ? this.container.querySelector(this.options.dragHandle) as HTMLElement
            : this.container.querySelector('.rte-dialog-header') as HTMLElement;

        if (dragHandle) {
            dragHandle.style.cursor = 'move';
            dragHandle.addEventListener('mousedown', (e) => this.onDragStart(e));
        }

        // Setup resize handle in footer
        const footer = this.container.querySelector('.rte-dialog-footer') as HTMLElement;
        if (footer) {
            const resizeHandle = this.createResizeHandle();
            footer.appendChild(resizeHandle);
        }
    }

    private createResizeHandle(): HTMLElement {
        const handle = document.createElement('div');
        handle.className = 'rte-resize-handle';
        handle.style.cssText = `
            position: absolute;
            bottom: 0;
            right: 0;
            width: 20px;
            height: 20px;
            cursor: se-resize;
            background: linear-gradient(135deg, transparent 0%, transparent 60%, #999 60%, #999 100%);
            user-select: none;
            z-index: 10;
        `;
        handle.addEventListener('mousedown', (e) => this.onResizeStart(e));
        return handle;
    }

    private onDragStart(e: MouseEvent): void {
        if ((e.target as HTMLElement).tagName === 'BUTTON') return;

        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;

        const rect = this.container.getBoundingClientRect();
        this.dragStartLeft = rect.left;
        this.dragStartTop = rect.top;

        this.container.classList.add('dragging');
        this.options.onDragStart?.();

        this.dragMouseMoveHandler = (e) => this.onDragMove(e);
        this.dragMouseUpHandler = () => this.onDragEnd();

        document.addEventListener('mousemove', this.dragMouseMoveHandler);
        document.addEventListener('mouseup', this.dragMouseUpHandler);
        e.preventDefault();
    }

    private onDragMove(e: MouseEvent): void {
        if (!this.isDragging) return;

        const deltaX = e.clientX - this.dragStartX;
        const deltaY = e.clientY - this.dragStartY;

        this.container.style.left = `${this.dragStartLeft + deltaX}px`;
        this.container.style.top = `${this.dragStartTop + deltaY}px`;
    }

    private onDragEnd(): void {
        this.isDragging = false;
        this.container.classList.remove('dragging');
        this.options.onDragEnd?.();

        if (this.dragMouseMoveHandler) {
            document.removeEventListener('mousemove', this.dragMouseMoveHandler);
        }
        if (this.dragMouseUpHandler) {
            document.removeEventListener('mouseup', this.dragMouseUpHandler);
        }
        this.dragMouseMoveHandler = null;
        this.dragMouseUpHandler = null;
    }

    private onResizeStart(e: MouseEvent): void {
        this.isResizing = true;
        this.resizeStartX = e.clientX;
        this.resizeStartY = e.clientY;

        const rect = this.container.getBoundingClientRect();
        this.resizeStartWidth = rect.width;
        this.resizeStartHeight = rect.height;

        this.container.classList.add('resizing');
        this.options.onResizeStart?.();

        this.resizeMouseMoveHandler = (e) => this.onResizeMove(e);
        this.resizeMouseUpHandler = () => this.onResizeEnd();

        document.addEventListener('mousemove', this.resizeMouseMoveHandler);
        document.addEventListener('mouseup', this.resizeMouseUpHandler);
        e.preventDefault();
    }

    private onResizeMove(e: MouseEvent): void {
        if (!this.isResizing) return;

        const deltaX = e.clientX - this.resizeStartX;
        const deltaY = e.clientY - this.resizeStartY;

        const newWidth = Math.max(this.options.minWidth!, this.resizeStartWidth + deltaX);
        const newHeight = Math.max(this.options.minHeight!, this.resizeStartHeight + deltaY);

        this.container.style.width = `${newWidth}px`;
        this.container.style.height = `${newHeight}px`;
    }

    private onResizeEnd(): void {
        this.isResizing = false;
        this.container.classList.remove('resizing');
        this.options.onResizeEnd?.();

        if (this.resizeMouseMoveHandler) {
            document.removeEventListener('mousemove', this.resizeMouseMoveHandler);
        }
        if (this.resizeMouseUpHandler) {
            document.removeEventListener('mouseup', this.resizeMouseUpHandler);
        }
        this.resizeMouseMoveHandler = null;
        this.resizeMouseUpHandler = null;
    }

    public reset(): void {
        this.container.style.left = 'auto';
        this.container.style.top = 'auto';
        this.container.style.width = 'auto';
        this.container.style.height = 'auto';
        this.container.style.maxWidth = '';
        this.container.style.maxHeight = '';
        this.container.style.boxSizing = '';
        this.container.classList.remove('dragging', 'resizing');
    }

    public destroy(): void {
        this.isDragging = false;
        this.isResizing = false;

        // Clean up event listeners
        if (this.dragMouseMoveHandler) {
            document.removeEventListener('mousemove', this.dragMouseMoveHandler);
        }
        if (this.dragMouseUpHandler) {
            document.removeEventListener('mouseup', this.dragMouseUpHandler);
        }
        if (this.resizeMouseMoveHandler) {
            document.removeEventListener('mousemove', this.resizeMouseMoveHandler);
        }
        if (this.resizeMouseUpHandler) {
            document.removeEventListener('mouseup', this.resizeMouseUpHandler);
        }
    }
}

// Export for Blazor interop
/* window['_zaa_services'] = window['_zaa_services'] || {};
window['_zaa_services']['draggableResizable'] = {
    create: (selector: string, options?: DragResizeOptions) => {
        return new DraggableResizable(selector, options);
    }
};*/

// Instance map to track created instances
const instanceMap = new Map<string, DraggableResizable>();

// Export for Blazor interop
window['_zaa_services'] = window['_zaa_services'] || {};
window['_zaa_services']['draggableResizable'] = {
    create: (selector: string, options?: DragResizeOptions) => {
        const instance = new DraggableResizable(selector, options);
        instanceMap.set(selector, instance);
        return instance;
    },
    destroy: (selector: string) => {
        const instance = instanceMap.get(selector);
        if (instance) {
            instance.destroy();
            instanceMap.delete(selector);
        }
    }
};

export default DraggableResizable;